import { apiFetch } from '@/api/client'
import type { Role } from '@/auth/roles'

/**
 * Derived by the backend from Keycloak, never stored as such — see
 * `docs/authentication.md#status` in wp-lead-backend.
 */
export const UserStatus = {
  /** Invited, mail sent, has not set a password yet. */
  INVITED: 'INVITED',
  ACTIVE: 'ACTIVE',
  /** Disabled by an administrator. Reversible. */
  LOCKED: 'LOCKED',
  /** Soft-deleted: disabled and hidden from the list, but still in Keycloak. */
  DELETED: 'DELETED',
} as const

export type UserStatus = (typeof UserStatus)[keyof typeof UserStatus]

export type ManagedUser = {
  id: string
  email: string | null
  firstName: string | null
  lastName: string | null
  /** The highest role assigned in Keycloak, `ROLE_`-prefixed. Null if the account holds none. */
  role: string | null
  status: UserStatus
  /**
   * Whether anybody has proved the address works. False for an account that has been invited but
   * has not used its link, and for one whose address an administrator has just changed.
   */
  emailVerified: boolean
  createdAt: string | null
}

export type ManagedUserPage = {
  users: ManagedUser[]
  page: number
  size: number
  total: number
}

/** False when the account was created but the invitation mail could not be sent. */
export type InvitationResult = {
  user: ManagedUser
  invitationSent: boolean
}

export type UpdateResult = {
  user: ManagedUser
  /**
   * Null when the address did not change and so nothing was sent — which is **not** the same as an
   * attempt that failed. False means the address changed but the verification mail did not go out;
   * `resendEmailVerification` is how it is tried again.
   */
  verificationSent: boolean | null
}

export type UserListQuery = {
  search?: string
  includeDeleted?: boolean
  page?: number
  size?: number
}

export function listUsers(query: UserListQuery = {}, signal?: AbortSignal): Promise<ManagedUserPage> {
  const parameters = new URLSearchParams()
  if (query.search) {
    parameters.set('search', query.search)
  }
  if (query.includeDeleted) {
    parameters.set('includeDeleted', 'true')
  }
  parameters.set('page', String(query.page ?? 0))
  parameters.set('size', String(query.size ?? DEFAULT_PAGE_SIZE))
  return apiFetch<ManagedUserPage>(`/api/admin/users?${parameters}`, { signal })
}

export const DEFAULT_PAGE_SIZE = 20

export function inviteUser(body: {
  email: string
  firstName: string
  lastName: string
  role: Role
}): Promise<InvitationResult> {
  return apiFetch<InvitationResult>('/api/admin/users', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export function resendInvitation(id: string): Promise<ManagedUser> {
  return apiFetch<ManagedUser>(`/api/admin/users/${encodeURIComponent(id)}/invitation`, { method: 'POST' })
}

/**
 * The address is also the **login** — the realm derives the username from it. Changing it ends the
 * user's sessions, the old address stops working, and a verification link goes to the new one.
 */
export function updateUser(
  id: string,
  body: { email: string; firstName: string; lastName: string; role: Role },
): Promise<UpdateResult> {
  return apiFetch<UpdateResult>(`/api/admin/users/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

/**
 * A second verification link for an address nobody has proved yet. Refused with
 * `EMAIL_ALREADY_VERIFIED` once it is proved, and with `TOO_MANY_REQUESTS` (429) when one went out
 * a moment ago — the rate limit, not the verified guard, is what stops this being used to mail
 * somebody repeatedly.
 */
export function resendEmailVerification(id: string): Promise<ManagedUser> {
  return apiFetch<ManagedUser>(`/api/admin/users/${encodeURIComponent(id)}/email-verification`, {
    method: 'POST',
  })
}

/**
 * What becomes of the leads assigned to an account that is being locked or deleted — somebody who
 * cannot log in cannot work them.
 */
export const LeadHandoverMode = {
  /**
   * Each back to whoever wrote it. The ones the account wrote itself, or whose writer is no longer
   * active, go to `assigneeId` — or to nobody without one.
   */
  OWNERS: 'OWNERS',
  /** Every one of them to `assigneeId`. */
  USER: 'USER',
  /** Every assignment taken back; the writers keep seeing their leads. */
  NOBODY: 'NOBODY',
} as const

export type LeadHandoverMode = (typeof LeadHandoverMode)[keyof typeof LeadHandoverMode]

export type LeadHandover = {
  mode: LeadHandoverMode
  assigneeId?: string
}

/**
 * What a handover would move. `assigned - returnable` is how many have nobody to go back to under
 * `OWNERS`, and so need a colleague named for them.
 */
export type LeadHandoverPreview = {
  assigned: number
  returnable: number
}

export function getLeadHandover(id: string, signal?: AbortSignal): Promise<LeadHandoverPreview> {
  return apiFetch<LeadHandoverPreview>(`/api/admin/users/${encodeURIComponent(id)}/lead-handover`, {
    signal,
  })
}

/**
 * Query parameters rather than a body, so that lock and delete take the same shape — a body on a
 * `DELETE` is dropped by more than one proxy. Left out entirely, the backend hands the leads back
 * to their writers and leaves the rest unassigned.
 */
function handoverQuery(handover?: LeadHandover): string {
  if (!handover) {
    return ''
  }
  const parameters = new URLSearchParams({ handover: handover.mode })
  if (handover.assigneeId) {
    parameters.set('assigneeId', handover.assigneeId)
  }
  return `?${parameters}`
}

export function lockUser(id: string, handover?: LeadHandover): Promise<ManagedUser> {
  return apiFetch<ManagedUser>(
    `/api/admin/users/${encodeURIComponent(id)}/lock${handoverQuery(handover)}`,
    { method: 'POST' },
  )
}

/** Re-enables a locked account, and restores a deleted one. */
export function unlockUser(id: string): Promise<ManagedUser> {
  return apiFetch<ManagedUser>(`/api/admin/users/${encodeURIComponent(id)}/unlock`, { method: 'POST' })
}

/**
 * A soft delete: the account is disabled and hidden, not removed. `unlockUser` brings it back —
 * the leads handed on stay where they went.
 */
export function deleteUser(id: string, handover?: LeadHandover): Promise<ManagedUser> {
  return apiFetch<ManagedUser>(`/api/admin/users/${encodeURIComponent(id)}${handoverQuery(handover)}`, {
    method: 'DELETE',
  })
}

/** @param temporary Keycloak forces a change at the next login. The safe default. */
export function setUserPassword(
  id: string,
  body: { password: string; temporary: boolean },
): Promise<ManagedUser> {
  return apiFetch<ManagedUser>(`/api/admin/users/${encodeURIComponent(id)}/password`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

/**
 * Public — the caller has no token, which is the whole point. Resolves the same way whether or not
 * the address belongs to an account: the backend answers 202 either way, so that this cannot be
 * used to find out who has one.
 */
export function requestPasswordReset(email: string): Promise<void> {
  return apiFetch<void>('/api/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
}
